import { Flex, Text, Link as ChakraLink, Button, Box } from '@chakra-ui/react';
import Image from 'next/image';
import Link from 'next/link';
import { configFile } from '@/config';

interface Props {
  author: any,
  isAuthorPage?: boolean,
  publicationNumber?: number
}

export default function AuthorCardHorizontal({ author, isAuthorPage, publicationNumber }: Props) {
  return (
    <Flex
      maxWidth="1216px"
      m="0 auto"
      width="100%"
      textAlign="center"
      boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)"
      borderRadius="8px"
      overflow="hidden"
      bgColor="#FFF"
      padding={{ base: "32px", lg: "56px" }}
    >
      <Flex
        alignItems="center"
        justifyContent="flex-start"
        gap={{ base: "32px", lg: "48px" }}
        m="0 auto"
        direction={{ base: 'column', md: 'row' }}
      >
        <Flex
          direction="column"
          gap="16px"
          flexGrow="1"
          minWidth="122px"
          w={{ base: '100%', md: 'auto' }}
          alignItems="center"
        >
          <Image
            width="122"
            height="122"
            style={{
              borderRadius: '9999px',
              objectFit: 'cover',
              width: '122px',
              height: '122px',
            }}
            alt={author.attributes.name}
            src={author.attributes.img.data.attributes.url}
          />
          {!isAuthorPage && (
            <Button
              aria-label="perfil page"
              width="100%"
              bgColor={configFile.primaryAccentColor}
              color="#FFF"
              _hover={{ bgColor: configFile.secondaryAccentColor }}
            >
              <ChakraLink as={Link} href={`/blog/perfil/${author.attributes.url}`}>
                Ver perfil
              </ChakraLink>
            </Button>
          )}
        </Flex>
        <Flex width="100%" direction="column" flexGrow="2" maxW="704px" alignItems="flex-start" h="100%">
          {isAuthorPage ? (
            <>
              <Flex gap="16px" direction={{ base: "column", lg: "row" }}>
                <Text fontWeight="800" fontSize="36px" lineHeight="40px" color="gray.800">
                  {author.attributes.name}
                </Text>
                <Box
                  as="span"
                  bgColor="purple.50"
                  padding="10px"
                  borderRadius="4px"
                  width="fit-content"
                >
                  <Text
                    fontSize="16px"
                    lineHeight="24px"
                    fontWeight="400"
                    color="purple.1000"
                  >
                    {publicationNumber} publicações
                  </Text>
                </Box>
              </Flex>
              <Text fontSize="18px" color="gray.600" lineHeight="28px" fontWeight="500" mt="4px" mb="24px">
                {author.attributes.jobTitle}
              </Text>
            </>
          ) : (
            <>
              <Text fontWeight="700" color="gray.800" fontSize="16px" lineHeight="24px">
                {author.attributes.name}
              </Text>
              <Text fontSize="14px" color="gray.600" lineHeight="20px" fontWeight="400" mt="4px" mb="24px">
                {author.attributes.jobTitle}
              </Text>
            </>
          )}
          <Text fontWeight={400} color="gray.600" textAlign="start" lineHeight="20px" fontSize="14px">
            {author.attributes.description}
          </Text>
        </Flex>
        <Flex
          direction="column"
          gap="24px"
          flexGrow="1"
          alignItems="flex-start"
          h="100%"
          minW="128px"
          w={{ base: '100%', md: 'auto' }}
        >
          <Text fontWeight="700" color="gray.800" fontSize="16px" lineHeight="24px">
            Social Media
          </Text>

          <Flex direction="column" alignItems="flex-start" gap="12px">
            {author.attributes.instagram !== null && (
              <ChakraLink
                aria-label='instagram page'
                as={Link}
                target="_blank"
                rel="noopener noreferrer"
                width="100%"
                height="24px"
                borderRadius="100%"
                display="flex"
                alignItems="center"
                href={`${author.attributes.instagram}`}
              >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3.40625C6 3.40625 4.40625 5.03125 4.40625 7C4.40625 9 6 10.5938 8 10.5938C9.96875 10.5938 11.5938 9 11.5938 7C11.5938 5.03125 9.96875 3.40625 8 3.40625ZM8 9.34375C6.71875 9.34375 5.65625 8.3125 5.65625 7C5.65625 5.71875 6.6875 4.6875 8 4.6875C9.28125 4.6875 10.3125 5.71875 10.3125 7C10.3125 8.3125 9.28125 9.34375 8 9.34375ZM12.5625 3.28125C12.5625 3.75 12.1875 4.125 11.7188 4.125C11.25 4.125 10.875 3.75 10.875 3.28125C10.875 2.8125 11.25 2.4375 11.7188 2.4375C12.1875 2.4375 12.5625 2.8125 12.5625 3.28125ZM14.9375 4.125C14.875 3 14.625 2 13.8125 1.1875C13 0.375 12 0.125 10.875 0.0625C9.71875 0 6.25 0 5.09375 0.0625C3.96875 0.125 3 0.375 2.15625 1.1875C1.34375 2 1.09375 3 1.03125 4.125C0.96875 5.28125 0.96875 8.75 1.03125 9.90625C1.09375 11.0312 1.34375 12 2.15625 12.8438C3 13.6562 3.96875 13.9062 5.09375 13.9688C6.25 14.0312 9.71875 14.0312 10.875 13.9688C12 13.9062 13 13.6562 13.8125 12.8438C14.625 12 14.875 11.0312 14.9375 9.90625C15 8.75 15 5.28125 14.9375 4.125ZM13.4375 11.125C13.2188 11.75 12.7188 12.2188 12.125 12.4688C11.1875 12.8438 9 12.75 8 12.75C6.96875 12.75 4.78125 12.8438 3.875 12.4688C3.25 12.2188 2.78125 11.75 2.53125 11.125C2.15625 10.2188 2.25 8.03125 2.25 7C2.25 6 2.15625 3.8125 2.53125 2.875C2.78125 2.28125 3.25 1.8125 3.875 1.5625C4.78125 1.1875 6.96875 1.28125 8 1.28125C9 1.28125 11.1875 1.1875 12.125 1.5625C12.7188 1.78125 13.1875 2.28125 13.4375 2.875C13.8125 3.8125 13.7188 6 13.7188 7C13.7188 8.03125 13.8125 10.2188 13.4375 11.125Z" fill="#C13584" />
                </svg>
                <Text color="gray.600" fontSize="16px" lineHeight="28px" ml="4px">
                  Instagram
                </Text>
              </ChakraLink>
            )}
            {author.attributes.twitter !== null && (
              <ChakraLink
                aria-label="twitter page"
                target="_blank"
                rel="noopener noreferrer"
                width="100%"
                height="20px"
                borderRadius="100%"
                display="flex"
                alignItems="center"
                as={Link}
                href={`${author.attributes.twitter}`}
              >
                <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.3438 3.75C14.3438 3.90625 14.3438 4.03125 14.3438 4.1875C14.3438 8.53125 11.0625 13.5 5.03125 13.5C3.15625 13.5 1.4375 12.9688 0 12.0312C0.25 12.0625 0.5 12.0938 0.78125 12.0938C2.3125 12.0938 3.71875 11.5625 4.84375 10.6875C3.40625 10.6562 2.1875 9.71875 1.78125 8.40625C2 8.4375 2.1875 8.46875 2.40625 8.46875C2.6875 8.46875 3 8.40625 3.25 8.34375C1.75 8.03125 0.625 6.71875 0.625 5.125V5.09375C1.0625 5.34375 1.59375 5.46875 2.125 5.5C1.21875 4.90625 0.65625 3.90625 0.65625 2.78125C0.65625 2.15625 0.8125 1.59375 1.09375 1.125C2.71875 3.09375 5.15625 4.40625 7.875 4.5625C7.8125 4.3125 7.78125 4.0625 7.78125 3.8125C7.78125 2 9.25 0.53125 11.0625 0.53125C12 0.53125 12.8438 0.90625 13.4688 1.5625C14.1875 1.40625 14.9062 1.125 15.5312 0.75C15.2812 1.53125 14.7812 2.15625 14.0938 2.5625C14.75 2.5 15.4062 2.3125 15.9688 2.0625C15.5312 2.71875 14.9688 3.28125 14.3438 3.75Z" fill="#1DA1F2" />
                </svg>

                <Text color="gray.600" fontSize="16px" lineHeight="28px" ml="4px">
                  Twitter
                </Text>
              </ChakraLink>
            )}
            {author.attributes.linkedin !== null && (
              <ChakraLink
                aria-label="linkedin page"
                target="_blank"
                rel="noopener noreferrer"
                width="100%"
                height="28px"
                display="flex"
                alignItems="center"
                _hover={{ fontStyle: 'none' }}
                as={Link}
                href={`${author.attributes.linkedin}`}
              >
                <svg width="16" height="16" viewBox="0 5 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 8.219v15.563c0 1.469 1.156 2.625 2.625 2.625h15.563c0.719 0 1.406-0.344 1.844-0.781 0.469-0.469 0.781-1.063 0.781-1.844v-15.563c0-1.469-1.156-2.625-2.625-2.625h-15.563c-0.781 0-1.375 0.313-1.844 0.781-0.438 0.438-0.781 1.125-0.781 1.844zM2.813 10.281c0-1 0.813-1.875 1.813-1.875 1.031 0 1.875 0.875 1.875 1.875 0 1.031-0.844 1.844-1.875 1.844-1 0-1.813-0.813-1.813-1.844zM7.844 23.125v-9.531c0-0.219 0.219-0.406 0.375-0.406h2.656c0.375 0 0.375 0.438 0.375 0.719 0.75-0.75 1.719-0.938 2.719-0.938 2.438 0 4 1.156 4 3.719v6.438c0 0.219-0.188 0.406-0.375 0.406h-2.75c-0.219 0-0.375-0.219-0.375-0.406v-5.813c0-0.969-0.281-1.5-1.375-1.5-1.375 0-1.719 0.906-1.719 2.125v5.188c0 0.219-0.219 0.406-0.438 0.406h-2.719c-0.156 0-0.375-0.219-0.375-0.406zM2.875 23.125v-9.531c0-0.219 0.219-0.406 0.375-0.406h2.719c0.25 0 0.406 0.156 0.406 0.406v9.531c0 0.219-0.188 0.406-0.406 0.406h-2.719c-0.188 0-0.375-0.219-0.375-0.406z"
                    fill="#0A66C2"
                  />
                </svg>
                <Text color="gray.600" fontSize="16px" lineHeight="28px" ml="4px">
                  LinkedIn
                </Text>
              </ChakraLink>
            )}
            {author.attributes.website !== null && (
              <ChakraLink
                aria-label="Website"
                target="_blank"
                rel="noopener noreferrer"
                height="28px"
                display="flex"
                alignItems="center"
                _hover={{ fontStyle: 'none' }}
                as={Link}
                href={`${author.attributes.website}`}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11 8C11 8.71875 10.9375 9.375 10.875 10H5.09375C5.03125 9.375 4.96875 8.71875 4.96875 8C4.96875 7.3125 5.03125 6.65625 5.09375 6H10.875C10.9375 6.65625 11 7.3125 11 8ZM15.7188 6C15.9062 6.65625 16 7.3125 16 8C16 8.71875 15.9062 9.375 15.7188 10H11.875C11.9375 9.375 12 8.6875 12 8C12 7.3125 11.9375 6.65625 11.875 6H15.7188ZM15.4062 5H11.75C11.4375 3.03125 10.8125 1.34375 10.0312 0.28125C12.4688 0.9375 14.4688 2.6875 15.4062 5ZM10.75 5H5.21875C5.40625 3.875 5.71875 2.875 6.0625 2.0625C6.40625 1.3125 6.75 0.78125 7.125 0.4375C7.46875 0.125 7.75 0 8 0C8.21875 0 8.5 0.125 8.84375 0.4375C9.21875 0.78125 9.5625 1.3125 9.90625 2.0625C10.25 2.875 10.5625 3.875 10.75 5ZM0.5625 5C1.5 2.6875 3.5 0.9375 5.9375 0.28125C5.15625 1.34375 4.53125 3.03125 4.21875 5H0.5625ZM4.09375 6C4.03125 6.65625 3.96875 7.3125 3.96875 8C3.96875 8.6875 4.03125 9.375 4.09375 10H0.25C0.0625 9.375 0 8.71875 0 8C0 7.3125 0.0625 6.65625 0.25 6H4.09375ZM6.0625 13.9688C5.71875 13.1562 5.40625 12.1562 5.21875 11H10.75C10.5625 12.1562 10.25 13.1562 9.90625 13.9688C9.5625 14.7188 9.21875 15.25 8.84375 15.5938C8.5 15.9062 8.21875 16 7.96875 16C7.75 16 7.46875 15.9062 7.125 15.5938C6.75 15.25 6.40625 14.7188 6.0625 13.9688ZM5.9375 15.75C3.5 15.0938 1.5 13.3438 0.5625 11H4.21875C4.53125 13 5.15625 14.6875 5.9375 15.75ZM10.0312 15.75C10.8125 14.6875 11.4375 13 11.75 11H15.4062C14.4688 13.3438 12.4688 15.0938 10.0312 15.75Z" fill="#2F4F4F" />
                </svg>

                <Text color="gray.600" fontSize="16px" lineHeight="28px" ml="4px">
                  Website
                </Text>
              </ChakraLink>
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
